import { authErrorMessage } from '@/lib/auth-error-message';
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useLocation } from 'wouter';
import { User, LoginCredentials, ImpersonationState } from '@/types/auth';
import type { OnboardingStatus } from '@/types/organization';
import { apiClient, errorCode } from '@/lib/api-client';
import { authService } from '@/services/api';
import { organizationService } from '@/services/api/organization.service';
import { platformService } from '@/services/api/platform.service';
import { tokenStore } from '@/lib/token-store';
import { homePathForRole } from './role-home';
import { clearConsentReturn, takeConsentReturn } from '@/features/connectedApps/consentReturn';
import { clearDeepLink, takeDeepLinkPath } from '@/features/activationEmails/deepLink';
import { beginSettling, endSettling, markNavigated } from '@/features/authGate/gateStore';
import { handOffOnboarding } from '@/features/authGate/onboardingHandoff';
import { seedFlags } from '@/hooks/useFlags';
import { QueryClientContext } from '@tanstack/react-query';
import { clearStash, returnPath, writeStash } from '@/lib/impersonation-stash';
import { isPlatformToken, jwtClaims } from '@/lib/jwt';
import { SESSION_EXPIRED_EVENT, type SessionExpired } from '@/lib/session-events';
import { isNative } from '@/platform/native';
import { nativeSessionBridge } from '@/native/session-bridge';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  /** The app: why the session ended (shown on the sign-in screen). Always null on the web. */
  notice?: string | null;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /**
   * Takes the session that was just stored (sign-up, Google): reads it, then moves the router to `to`, as login() does; the auth gate covers the way.
   * No page reload, and nothing (the sign-in page's own redirect, a guard) acts on the new user before the move. Throws when the session cannot be read.
   */
  adoptSession: (to: string, onboarding?: OnboardingStatus) => Promise<void>;
  resendConfirmationEmail: (email: string) => Promise<void>;
  clearError: () => void;
  impersonation: ImpersonationState | null;
  startImpersonation: (userId: number, opts?: { reason?: string; accountId?: number; accountName?: string }) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** The app's sign-out question while outbox items wait (`pending` < 0: the count could not be read). */
function signOutPrompt(pending: number, storageDegraded: boolean): string {
  const counted = pending > 0 ? `${pending} ${pending === 1 ? 'item has' : 'items have'} not been sent yet.` : '';
  if (storageDegraded) {
    // the database could not be opened: its items are not counted, and new ones live only in memory
    const lead = counted || (pending < 0 ? 'Some items may not have been sent yet.' : '');
    return `${lead ? `${lead} ` : ''}The app could not save to this phone's storage, so unsent items are kept only while the app stays open. Sign out anyway?`;
  }
  const what = counted || 'Some items may not have been sent yet.';
  return `${what} They stay on this phone and are sent when you sign in again (within 7 days). Sign out anyway?`;
}

const isUnauthorized = (error: unknown) => (error as { response?: { status?: number } })?.response?.status === 401;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // Nothing stored: nothing to restore, so a signed-out visitor is never held up (no loading screen, no flash on the sign-in form). A stored session: loading until it is read.
  const [loading, setLoading] = useState(() => Boolean(isNative() ? tokenStore.getRefresh() : tokenStore.getAccess()));
  const [error, setError] = useState<string | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  // The query cache belongs to the person who signed in: it is emptied on every sign-out and before every sign-in, so one person's cached lists, flags and
  // home data can never be shown to the next in the same tab (a page reload used to do this for Google and sign-up). Tests may render the provider alone.
  const queryClient = useContext(QueryClientContext);

  /**
   * The app's cold start: the access token is in memory only, so a stored refresh token means a session. It is refreshed
   * first; offline, the cached profile keeps the user signed in. Only a refusal from the server signs them out.
   */
  const checkNativeAuth = useCallback(async () => {
    if (!tokenStore.getRefresh()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      if (!tokenStore.getAccess()) await apiClient.refreshSession();
      const session = await authService.getSession();
      seedFlags(queryClient, session.flags);
      setUser(session.user);
    } catch (error) {
      if (isUnauthorized(error) || !tokenStore.getRefresh()) {
        apiClient.clearAuth();
        setUser(null);
      } else {
        setUser(nativeSessionBridge()?.cachedProfile() ?? null);
      }
    }
    setLoading(false);
  }, [queryClient]);

  const checkAuth = useCallback(async () => {
    if (isNative()) return checkNativeAuth();
    try {
      const accessToken = tokenStore.getAccess();

      if (!accessToken) {
        setUser(null);
        setImpersonation(null);
        setLoading(false);
        return;
      }

      // A platform-admin token has no tenant user: /auth/me would refuse it and sign the admin out of the console.
      if (isPlatformToken(accessToken) && !apiClient.isImpersonating) {
        setUser(null);
        setImpersonation(null);
        setLoading(false);
        return;
      }

      const session = await authService.getSession();
      // the session answer already carries this person's flags: the same ones GET /v1/flags would return, so that request is not made
      seedFlags(queryClient, session.flags);
      setUser(session.user);
      setImpersonation(session.impersonation);
      setLoading(false);
    } catch (error) {
      // Only clear auth if it's an actual auth error, not network issue
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.status === 401) {
          apiClient.clearAuth();
          setUser(null);
        }
      }
      setLoading(false);
    }
  }, [checkNativeAuth, queryClient]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // The app: the outbox sends only the signed-in user's items, and the profile is cached for an offline cold start.
  useEffect(() => {
    const bridge = isNative() ? nativeSessionBridge() : null;
    if (!bridge) return;
    if (!user) {
      bridge.setIdentity(null);
      return;
    }
    const orgId = user.organizationId || Number(jwtClaims(tokenStore.getAccess())?.organization_id) || 0;
    bridge.setIdentity(orgId ? { userId: user.id, orgId } : null);
    // cached with the org, which an offline cold start (no token claims yet) cannot recover
    bridge.saveProfile(orgId ? { ...user, organizationId: orgId } : user);
  }, [user]);

  // The app: the server ended the session (api-client cleared the tokens). To the sign-in screen through the router, never a
  // reload, so the outbox keeps its items.
  useEffect(() => {
    if (!isNative()) return;
    const onExpired = (event: Event) => {
      queryClient?.clear();
      setUser(null);
      setImpersonation(null);
      setNotice((event as CustomEvent<SessionExpired>).detail?.message ?? null);
      setLocation('/login');
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [setLocation, queryClient]);

  const login = async (credentials: LoginCredentials) => {
    try {
      setError(null);
      setLoading(true);
      // the one loading screen from the click until the destination is ready (AuthGate); a failure below puts the form back
      beginSettling();
      const go = (path: string) => {
        setLocation(path);
        markNavigated();
      };

      const { user, accessToken, refreshToken } = await authService.login(credentials);

      // the previous person's cached data goes before anything of the new session is stored or shown
      queryClient?.clear();

      // Set tokens first and ensure they're persisted
      apiClient.setAuthTokens(accessToken, refreshToken);

      // Verify tokens are set before proceeding
      const tokenSet = tokenStore.getAccess();

      if (!tokenSet) {
        throw new Error('Authentication tokens were not properly set');
      }

      setUser(user);
      setNotice(null);

      // An app's consent screen was waiting for this sign-in (Zapier, ChatGPT, Claude): back to it, before the role's home or onboarding
      const consent = takeConsentReturn();
      if (consent) {
        go(consent);
        return;
      }

      // For admin users, check onboarding status
      if (user.role === 'admin') {
        try {
          const { onboarding } = await organizationService.getOnboardingStatus();
          if (!onboarding.completed && !onboarding.skipped) {
            go('/admin/onboarding');
            return;
          }
          // the owner area asks the same question on arrival (ProtectedRoute): it reuses this answer
          handOffOnboarding(queryClient, onboarding);
        } catch (error) {
          console.error('Failed to check onboarding status:', error);
          // Continue with normal redirect if onboarding check fails
        }
      }

      // A setup email's link (payments, business details, new quote) was waiting for this sign-in: the owner goes there (an owner who has not finished first run went to it above and keeps the link)
      const deep = takeDeepLinkPath();
      if (deep && user.role === 'admin') {
        go(deep);
        return;
      }

      // Redirect based on role
      const redirectPath = homePathForRole(user.role);
      go(redirectPath);
    } catch (error: any) {
      endSettling();
      // Extract error message from backend response
      const message = authErrorMessage(error, 'Login failed');
      setError(message);
      // the machine code and status too: the app's login screen words a refused customer (mobile_role_not_allowed), a business without the app (404) and a busy server (503) itself
      throw Object.assign(new Error(message), { code: errorCode(error.response?.data), status: error.response?.status });
    } finally {
      setLoading(false);
    }
  };

  // The app: ask first while outbox items wait (they stay bound to this user), then revoke the device session, then clear.
  const logoutNative = async () => {
    const bridge = nativeSessionBridge();
    if (user && bridge) {
      const degraded = bridge.storageDegraded?.() === true;
      const pending = await bridge.countPending(user.id).catch(() => -1);
      if ((pending !== 0 || degraded) && !window.confirm(signOutPrompt(pending, degraded))) return;
    }
    try {
      setLoading(true);
      await authService.logout();
    } catch {
      // Ignore logout errors (offline: the session is still cleared here)
    } finally {
      apiClient.clearAuth();
      queryClient?.clear();
      setUser(null);
      setNotice(null);
      setLocation('/login');
      setLoading(false);
    }
  };

  const logout = async () => {
    if (isNative()) return logoutNative();
    try {
      setLoading(true);
      await authService.logout();
    } catch {
      // Ignore logout errors
    } finally {
      apiClient.clearAuth();
      clearConsentReturn();
      clearDeepLink();
      queryClient?.clear();
      setUser(null);
      setLocation('/');
      setLoading(false);
    }
  };

  const adoptSession = async (to: string, onboarding?: OnboardingStatus) => {
    setLoading(true);
    try {
      const session = await authService.getSession();
      // the previous person's cached data goes before the new user is set or any screen renders
      queryClient?.clear();
      seedFlags(queryClient, session.flags);
      if (onboarding) handOffOnboarding(queryClient, onboarding);
      setUser(session.user);
      setImpersonation(session.impersonation);
      setLocation(to);
      markNavigated();
    } catch (error) {
      // the tokens were stored for a session that cannot be read: leave no half session behind (a reload would otherwise sign the person in while the page says it failed)
      apiClient.clearAuth();
      setUser(null);
      setImpersonation(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      setUser(user);
    } catch {
      setUser(null);
    }
  };

  const resendConfirmationEmail = async (email: string) => {
    try {
      setError(null);
      await apiClient.instance.post('/v1/auth/resend-confirmation', { email });
    } catch (error: any) {
      const message = error.response?.data?.error?.message ||
                     error.response?.data?.message ||
                     error.message ||
                     'Failed to resend confirmation email';
      setError(message);
      throw new Error(message);
    }
  };

  const clearError = () => {
    setError(null);
  };

  // Super-admin: begin impersonating a target user. The platform token is
  // stashed by apiClient.enterImpersonation; @current_user becomes the target.
  const startImpersonation = async (userId: number, opts?: { reason?: string; accountId?: number; accountName?: string }) => {
    const result = await platformService.impersonate(userId, opts?.reason);
    apiClient.enterImpersonation(result.accessToken);
    // only now (a failed start must not leave a stash that names the wrong account)
    if (opts?.accountId) {
      const ends = result.expiresAt ? Date.parse(result.expiresAt) : NaN;
      writeStash({ accountId: opts.accountId, accountName: opts.accountName, sessionId: result.sessionId, expiresAt: Number.isNaN(ends) ? Date.now() + 30 * 60_000 : ends });
    } else {
      clearStash();
    }
    const session = await authService.getSession();
    // The token now belongs to the customer's account: whatever the previous identity left in the cache (limits, team, flags: keys that carry no account)
    // goes before the customer's user is rendered. The screens that stay mounted refetch under the new token on that render.
    queryClient?.clear();
    seedFlags(queryClient, session.flags);
    setUser(session.user);
    setImpersonation(session.impersonation);

    const role = session.user.role;
    const redirectPath = homePathForRole(role);
    setLocation(redirectPath);
  };

  // End impersonation: close the audit record (using the stashed platform token),
  // restore the platform session, and return to the picker.
  const stopImpersonation = async () => {
    const sessionId = impersonation?.sessionId;
    const platformToken = apiClient.getPlatformToken();
    apiClient.beginStoppingImpersonation();
    try {
      if (sessionId && platformToken) {
        try {
          await platformService.stopImpersonation(sessionId, platformToken);
        } catch (err) {
          // 401/404: the platform session or the impersonation is already gone, so leaving locally is right. Anything else:
          // keep the impersonation and both tokens so the banner can offer a retry.
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status !== 401 && status !== 404) throw err;
        }
      }
    } catch (err) {
      apiClient.doneStoppingImpersonation();
      throw err;
    }
    apiClient.exitImpersonation();
    // the customer's cached data goes with the customer's session
    queryClient?.clear();
    setImpersonation(null);
    setUser(null);
    const back = returnPath('/platform');
    clearStash();
    setLocation(back);
    // Customer calls still in flight are refused (the session is closed); give them a moment before a 401 counts as real again.
    window.setTimeout(() => apiClient.doneStoppingImpersonation(), 3000);
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    notice,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
    adoptSession,
    resendConfirmationEmail,
    clearError,
    impersonation,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Custom hook for protected routes
export function useRequireAuth(requiredRole?: User['role']) {
  const { user, loading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        setLocation('/login');
      } else if (requiredRole && user?.role !== requiredRole) {
        setLocation('/unauthorized');
      }
    }
  }, [loading, isAuthenticated, user, requiredRole, setLocation]);

  return { user, loading, isAuthenticated };
}
